// The firm's possible duplicates, on the screen that lists the firm's
// matters, for the admin the scan answers. A pair is two people until an
// admin says otherwise; the block says nothing when the scan finds nothing,
// and says so when it could not run.

import { useState } from "react";
import { EntityLink } from "../chrome/EntityLink";
import { MergeDialog } from "../matter/MergeDialog";
import { useDismissPair, useFirmDuplicates } from "../../hooks/useCaseTwins";
import { matchPhrase } from "../../lib/duplicateWords";
import type { DuplicateCase, FirmDuplicatePair } from "../../api/duplicates";

const FIRST = 10;
const ALL = 100;

const CARD = { padding: "12px 18px", marginBottom: 12, border: "1px solid var(--warn-600)" };
const ROW = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap" as const,
  marginTop: 6,
};

function Who({ c }: { c: DuplicateCase }) {
  return (
    <>
      <EntityLink kind="matter" id={c.caseId} eID={c.eId ?? c.caseId}>
        {c.name}
      </EntityLink>{" "}
      <code>{c.eId ?? "no eID"}</code>
    </>
  );
}

export function DuplicatesBlock() {
  const [all, setAll] = useState(false);
  const scan = useFirmDuplicates(all ? ALL : FIRST);
  const dismiss = useDismissPair();
  const [merging, setMerging] = useState<FirmDuplicatePair | null>(null);

  // The dialog sits beside the card in one tree shape whatever the scan says:
  // the pair stops being reported the moment the merge lands, and a remount
  // would take the summary with the row.
  const dialog = merging && (
    <MergeDialog
      open
      onClose={() => setMerging(null)}
      current={{ caseId: merging.case1.caseId, eId: merging.case1.eId }}
      twin={{ ...merging.case2, matchedBy: merging.matchedBy }}
    />
  );

  const data = scan.data;
  const shown = data?.groups.length ?? 0;
  const busyPair = dismiss.isPending ? dismiss.variables?.join(":") : null;
  const card = scan.isError ? (
    <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
      The duplicate scan could not run - try again later.
    </div>
  ) : !data || (data.total === 0 && data.oversized.length === 0) ? null : (
    <div className="cc-card" style={CARD}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <b style={{ color: "var(--warn-600)" }}>Possible duplicates</b>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {data.total} pair{data.total === 1 ? "" : "s"}
          {shown < data.total ? `, showing ${shown}` : ""}
        </span>
      </div>
      {data.groups.map((pair) => {
        const key = `${pair.case1.caseId}:${pair.case2.caseId}`;
        return (
          <div key={key} style={ROW}>
            <span>
              <Who c={pair.case1} /> and <Who c={pair.case2} /> have {matchPhrase(pair.matchedBy)}
              <span style={{ color: "var(--muted)" }}>
                {` ${String.fromCharCode(183)} ${pair.confidence}%`}
              </span>
            </span>
            <span style={{ display: "flex", gap: 8 }}>
              <button type="button" className="cc-link-btn" onClick={() => setMerging(pair)}>
                Merge...
              </button>
              <button
                type="button"
                className="cc-link-btn"
                disabled={busyPair === key}
                onClick={() => dismiss.mutate([pair.case1.caseId, pair.case2.caseId])}
              >
                Not the same person
              </button>
            </span>
          </div>
        );
      })}
      {dismiss.isError && (
        <div style={{ fontSize: 12, color: "var(--warn-600)", marginTop: 8 }}>
          The pair could not be marked - try again.
        </div>
      )}
      {data.oversized.map((o) => (
        <div
          key={o.sample.map((c) => c.caseId).join(":")}
          style={{ ...ROW, color: "var(--muted)" }}
        >
          <span>
            {o.count} matters have {matchPhrase([o.field])} - a data defect, not a duplicate:{" "}
            {o.sample.map((c, i) => (
              <span key={c.caseId}>
                {i > 0 ? ", " : ""}
                <Who c={c} />
              </span>
            ))}
            ...
          </span>
        </div>
      ))}
      {shown < data.total && !all && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="cc-link-btn" onClick={() => setAll(true)}>
            Show all {data.total}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {card}
      {dialog}
    </>
  );
}
